import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  placeholder = "Type your message...",
  disabled = false,
}) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      const messageToSend = message.trim();
      
      // Clear input immediately for better UX
      setMessage('');
      
      // Send message
      onSendMessage(messageToSend);
      
      // Keep focus on input after sending message
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  // Auto-focus on mount for better UX
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Refocus when disabled state changes (e.g., when AI finishes responding)
  useEffect(() => {
    if (!disabled) {
      // Small delay to ensure any UI updates are complete
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50); // Reduced delay for faster focus
    }
  }, [disabled]);

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Send message on Enter (but not Shift+Enter for potential multiline support)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 sm:gap-3 items-end">
      <div className="flex-1 relative">
        <input
          ref={inputRef}
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? "Sending..." : placeholder}
          disabled={disabled}
          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl glass-effect border text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-accent-coral/50 focus:border-accent-coral/50 transition-all duration-200 text-sm sm:text-base ${
            disabled 
              ? 'border-dark-700 bg-dark-800/30 cursor-not-allowed' 
              : 'border-dark-600 hover:border-dark-500'
          }`}
          autoComplete="off"
          spellCheck="false"
        />
        
        {/* Character count or status indicator */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="flex items-center space-x-2">
            {message.length > 0 && (
              <span className="text-xs text-dark-400">
                {message.length}
              </span>
            )}
          </div>
        </div>
      </div>

      <motion.button
        type="submit"
        disabled={!message.trim() || disabled}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`p-2.5 sm:p-3 rounded-2xl transition-all duration-300 ${
          message.trim() && !disabled
            ? 'bg-gradient-to-r from-accent-coral to-accent-peach text-white shadow-lg hover:shadow-xl'
            : 'bg-dark-700 text-dark-400 cursor-not-allowed'
        }`}
      >
        <svg
          className="w-4 h-4 sm:w-5 sm:h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
          />
        </svg>
      </motion.button>
    </form>
  );
};

export default ChatInput;